import AddIcon from "@mui/icons-material/Add";
import CancelScheduleSendOutlinedIcon from "@mui/icons-material/CancelScheduleSendOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import UploadFile from "components/Input/UploadFile";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import PendingPaymentDialog from "components/Payments/PendingPaymentDialog";
import { canChangePendingPayment } from "utils/pendingPaymentChange";
import {
  editPendingTermPayment,
  withdrawTermPayment,
} from "views/admin/Accounts/verification/verificationTaskApi";
import PaymentReceiptChips from "components/Payments/PaymentReceiptChips";
import { canVerifyPayments } from "utils/permissionUtils";
import { formatDateForInput } from "views/admin/Accounts/accountUtils";

import {
  addTermPayment,
  deleteCoeFeePayment,
  fetchCoeFeePayments,
  resubmitTermPayment,
} from "../admissionApi";
import CoeFeePaymentChoiceDialog from "./CoeFeePaymentChoiceDialog";
import { formatAudCompact } from "./termPaymentUtils";

const emptyDraft = () => ({
  amount: "",
  date: new Date().toISOString().split("T")[0],
  reference: "",
  notes: "",
});

const STATUS_CHIP = {
  PENDING_VERIFICATION: { label: "Awaiting verification", color: "warning" },
  VERIFIED: { label: "Verified", color: "success" },
  REJECTED: { label: "Rejected", color: "error" },
};

const statusChipFor = (payment) => {
  if (payment.recordOnly) return { label: "Recorded — not sent", color: "default" };
  return STATUS_CHIP[payment.status] ?? { label: "Not submitted", color: "default" };
};

/** Only money that never landed can be removed here; the rest goes through the super-admin path. */
const isDeletable = (payment) => payment.status === "NONE" || payment.status === "REJECTED";

const CoeFeePaymentRow = ({ payment, readOnly, onEditRejected, onDelete, onChangePending }) => {
  const attachments = payment.attachments || [];
  const chip = statusChipFor(payment);
  const rejected = payment.status === "REJECTED";

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap">
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2" fontWeight={600}>
              {formatAudCompact(Number(payment.amount) || 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateForInput(payment.date) || payment.date || "—"}
            </Typography>
            {payment.reference ? (
              <Chip label={payment.reference} size="small" variant="outlined" />
            ) : null}
            <Chip label={chip.label} size="small" color={chip.color} variant="outlined" />
          </Stack>

          {payment.status === "PENDING_VERIFICATION" && payment.lastEditedByName ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Edited by {payment.lastEditedByName} while pending
            </Typography>
          ) : null}

          {rejected && payment.rejectionFeedback ? (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              Rejection reason: {payment.rejectionFeedback}
            </Typography>
          ) : null}

          {payment.notes ? (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
            >
              {payment.notes}
            </Typography>
          ) : null}

          {attachments.length > 0 ? (
            <PaymentReceiptChips urls={attachments} sx={{ mt: 0.5 }} />
          ) : null}
        </Box>

        {canChangePendingPayment(payment) && (
          <Stack direction="row" gap={0.5}>
            <Tooltip title="Edit before it is verified">
              <IconButton size="small" onClick={() => onChangePending("edit", payment)}>
                <EditNoteOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Withdraw from verification">
              <IconButton
                size="small"
                color="error"
                onClick={() => onChangePending("withdraw", payment)}
              >
                <CancelScheduleSendOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {!readOnly && (
          <Stack direction="row" gap={0.5}>
            {rejected && (
              <Tooltip title="Edit and resubmit for verification">
                <IconButton size="small" color="warning" onClick={() => onEditRejected(payment)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isDeletable(payment) && (
              <Tooltip title="Delete this COE payment">
                <IconButton size="small" color="error" onClick={() => onDelete(payment)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

/**
 * The admission's COE fee, as a list of payments rather than one amount.
 *
 * The enrolment fee is often paid in parts, and it is paid before the COE exists — which is why
 * these are the one kind of term payment not gated on the COE having arrived. The first payment on
 * an empty term goes straight for verification; after that the user is asked whether the money is
 * new or already on the account, because only they know.
 */
export default function CoeFeePayments({
  term1Id,
  cadId,
  termHasOtherPayments = false,
  readOnly = false,
  legacyAmount = null,
  legacyDate = null,
  onChanged,
}) {
  const queryClient = useQueryClient();
  const canSelfVerify = canVerifyPayments();

  const [draft, setDraft] = useState(emptyDraft);
  const [uploadData, setUploadData] = useState({ attachments: [] });
  const [editingUuid, setEditingUuid] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [choice, setChoice] = useState({ open: false, mode: "VERIFY" });
  const [autoVerify, setAutoVerify] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, payment: null });
  const [pendingChange, setPendingChange] = useState({ mode: null, payment: null });

  const { data: payments = [], refetch } = useQuery({
    queryKey: ["coeFeePayments", term1Id],
    queryFn: () => fetchCoeFeePayments(term1Id),
    enabled: Boolean(term1Id),
    // The term schedule and the account both write payments, so a cached list goes stale as soon as
    // the user comes back from either.
    refetchOnMount: "always",
  });

  const total = useMemo(
    () => payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [payments]
  );

  const resetDraft = () => {
    setDraft(emptyDraft());
    setUploadData({ attachments: [] });
    setEditingUuid(null);
  };

  const afterChange = async () => {
    resetDraft();
    await refetch();
    if (cadId) await queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
    onChanged?.();
  };

  const validate = () => {
    if (!term1Id) {
      toast.info("Save Step 3 (Offer received) first so the term schedule exists.");
      return false;
    }
    const amount = Number(draft.amount);
    if (!draft.amount || Number.isNaN(amount) || amount <= 0) {
      toast.warning("Enter a COE payment amount greater than zero.");
      return false;
    }
    if (!draft.date) {
      toast.warning("Enter the COE payment date.");
      return false;
    }
    return true;
  };

  const payload = (recordOnly) => ({
    amount: String(Number(draft.amount)),
    date: draft.date,
    reference: draft.reference.trim(),
    notes: draft.notes.trim(),
    attachments: uploadData.attachments || [],
    coeFee: true,
    recordOnly,
    autoVerify: !recordOnly && canSelfVerify && autoVerify,
  });

  const submit = async (recordOnly) => {
    setIsSaving(true);
    try {
      await addTermPayment(term1Id, payload(recordOnly));
      setChoice({ open: false, mode: "VERIFY" });
      await afterChange();
      toast.success(
        recordOnly
          ? "COE payment recorded. It does not affect the account."
          : "COE payment saved and sent for verification."
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to save the COE payment."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = () => {
    if (!validate()) return;
    // Nothing on the term yet: this payment IS the money, so there is nothing to disambiguate.
    if (!termHasOtherPayments && payments.length === 0) {
      submit(false);
      return;
    }
    setChoice({ open: true, mode: "VERIFY" });
  };

  const handleResubmit = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      await resubmitTermPayment(term1Id, editingUuid, {
        amount: String(Number(draft.amount)),
        date: draft.date,
        reference: draft.reference.trim(),
        notes: draft.notes.trim(),
        attachments: uploadData.attachments || [],
      });
      await afterChange();
      toast.success("COE payment resubmitted for verification.");
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to resubmit the COE payment."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (payment) => {
    setEditingUuid(payment.uuid);
    setDraft({
      amount: payment.amount ?? "",
      date: formatDateForInput(payment.date) || payment.date || "",
      reference: payment.reference ?? "",
      notes: payment.notes ?? "",
    });
    setUploadData({ attachments: [...(payment.attachments || [])] });
  };

  const confirmDelete = async () => {
    const payment = deleteDialog.payment;
    if (!payment) return;
    setIsSaving(true);
    try {
      await deleteCoeFeePayment(payment.id);
      setDeleteDialog({ open: false, payment: null });
      if (editingUuid === payment.uuid) resetDraft();
      await afterChange();
      toast.success("COE payment deleted.");
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to delete the COE payment."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const showLegacyAmount = payments.length === 0 && Number(legacyAmount) > 0;

  return (
    <>
      <Stack direction="row" alignItems="baseline" gap={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" className="text-app-text font-semibold">
          COE fee payments
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total recorded: <strong>{formatAudCompact(total)}</strong>
          {payments.length > 0
            ? ` across ${payments.length} payment${payments.length === 1 ? "" : "s"}`
            : ""}
        </Typography>
      </Stack>

      {!term1Id && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Save Step 3 (Offer received) first — COE payments are recorded against Term 1.
        </Alert>
      )}

      {showLegacyAmount && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A COE fee of {formatAudCompact(Number(legacyAmount))}
          {legacyDate ? ` dated ${formatDateForInput(legacyDate) || legacyDate}` : ""} was recorded
          on this admission before COE payments were itemised. Add it below if it should appear as a
          payment.
        </Alert>
      )}

      {payments.length > 0 && (
        <Stack gap={1} sx={{ mb: 2 }}>
          {payments.map((payment) => (
            <CoeFeePaymentRow
              key={payment.id ?? payment.uuid}
              payment={payment}
              readOnly={readOnly}
              onEditRejected={startEdit}
              onDelete={(p) => setDeleteDialog({ open: true, payment: p })}
              onChangePending={(mode, p) =>
                setPendingChange({
                  mode,
                  payment: { ...p, date: formatDateForInput(p.date) || p.date || "" },
                })
              }
            />
          ))}
        </Stack>
      )}

      {!readOnly && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
            {editingUuid ? "Edit rejected COE payment" : "Add a COE payment"}
          </Typography>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TextField
              label="Amount (AUD)"
              size="small"
              fullWidth
              required
              type="number"
              inputProps={{ min: 0 }}
              value={draft.amount}
              onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
            />
            <TextField
              label="Payment date"
              type="date"
              size="small"
              fullWidth
              required
              inputProps={{ max: new Date().toISOString().split("T")[0] }}
              InputLabelProps={{ shrink: true }}
              value={draft.date}
              onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
            />
            <TextField
              label="Reference / method"
              size="small"
              fullWidth
              value={draft.reference}
              onChange={(e) => setDraft((d) => ({ ...d, reference: e.target.value }))}
            />
            <TextField
              label="Notes"
              size="small"
              multiline
              minRows={2}
              fullWidth
              className="sm:col-span-3"
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            />
          </div>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
              Receipts
            </Typography>
            <UploadFile
              data={uploadData}
              setData={setUploadData}
              imageKey="attachments"
              type="multiple"
              label="receipt or proof of payment"
            />
          </Box>

          <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={editingUuid ? null : <AddIcon />}
              disabled={isSaving || !term1Id}
              onClick={editingUuid ? handleResubmit : handleAdd}
            >
              {isSaving ? "Saving…" : editingUuid ? "Resubmit for verification" : "Add COE payment"}
            </Button>
            {editingUuid && (
              <Button variant="outlined" onClick={resetDraft} disabled={isSaving}>
                Cancel edit
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      <Divider className="my-6" />

      <CoeFeePaymentChoiceDialog
        open={choice.open}
        mode={choice.mode}
        onModeChange={(mode) => setChoice((c) => ({ ...c, mode }))}
        onClose={() => setChoice({ open: false, mode: "VERIFY" })}
        onConfirm={() => submit(choice.mode === "RECORD_ONLY")}
        isSubmitting={isSaving}
        amount={draft.amount}
        canSelfVerify={canSelfVerify}
        autoVerify={autoVerify}
        onAutoVerifyChange={setAutoVerify}
      />

      <PendingPaymentDialog
        open={Boolean(pendingChange.payment)}
        mode={pendingChange.mode}
        payment={pendingChange.payment}
        saveEdit={(payment, values) =>
          editPendingTermPayment(payment.id, {
            expectedRevision: payment.revision,
            amount: String(Number(values.amount)),
            date: values.date,
            reference: values.reference,
            notes: values.notes,
            attachments: values.attachments,
          })
        }
        withdraw={(payment, reason) =>
          withdrawTermPayment(payment.id, { expectedRevision: payment.revision, reason })
        }
        onClose={() => setPendingChange({ mode: null, payment: null })}
        onChanged={afterChange}
        onResubmit={
          term1Id
            ? (payment, values) =>
                resubmitTermPayment(term1Id, payment.uuid, {
                  amount: String(Number(values.amount)),
                  date: values.date,
                  reference: values.reference,
                  notes: values.notes,
                  attachments: values.attachments,
                })
            : undefined
        }
      />

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete COE payment"
        message={
          deleteDialog.payment
            ? `Delete the ${formatAudCompact(
                Number(deleteDialog.payment.amount) || 0
              )} COE payment? This removes the record permanently. No account figures change, because this payment was never verified.`
            : ""
        }
        confirmLabel="Delete"
        confirmColor="error"
        loading={isSaving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog({ open: false, payment: null })}
      />
    </>
  );
}
